import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Shield, Plus, Users, Crown, Mail, Clock, AlertCircle, CheckCircle, UserPlus } from 'lucide-react';
import { teamsAPI } from '../../services/api';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  permissions: Record<string, boolean>;
  status: 'active' | 'pending';
  joinedAt: string;
  avatar?: string;
}

interface TeamData {
  id: string;
  name: string;
  owner_id: string;
  members: TeamMember[];
  max_members: number;
  member_count: number;
  approval_required: boolean;
  default_timezone: string;
  shared_accounts: string[];
  created_at: string;
}

type UserRole = 'admin' | 'member';

export const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('member');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: teamData, isLoading, isError } = useQuery<TeamData>({
    queryKey: ['team'],
    queryFn: () => teamsAPI.get().then(r => r.data.team),
    staleTime: 5 * 60 * 1000,
  });

  const showMessage = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') {
      setSuccess(msg);
      setError(null);
      setTimeout(() => setSuccess(null), 4000);
    } else {
      setError(msg);
      setSuccess(null);
      setTimeout(() => setError(null), 5000);
    }
  };

  const inviteMemberMutation = useMutation({
    mutationFn: () =>
      teamsAPI.invite({
        email: inviteEmail,
        name: inviteName,
        role: inviteRole,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setShowInviteForm(false);
      resetForm();
      showMessage(t('team_invitation_sent', `Invitation sent to ${inviteEmail}`), 'success');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('team_invitation_failed', 'Failed to send invitation');
      showMessage(msg, 'error');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) =>
      teamsAPI.updateMemberRole(email, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      showMessage(t('team_role_updated', 'Member role updated'), 'success');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('team_role_update_failed', 'Failed to update role');
      showMessage(msg, 'error');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (email: string) => teamsAPI.removeMember(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      showMessage(t('team_member_removed', 'Member removed from team'), 'success');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('team_member_remove_failed', 'Failed to remove member');
      showMessage(msg, 'error');
    },
  });

  const resetForm = () => {
    setInviteEmail('');
    setInviteName('');
    setInviteRole('member');
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteName.trim()) return;
    await inviteMemberMutation.mutateAsync();
  };

  const owner = teamData?.members?.find((m: TeamMember) => m.role === 'owner');
  const members = teamData?.members?.filter((m: TeamMember) => m.role !== 'owner') || [];
  const slotsUsed = teamData?.member_count || 0;
  const slotsMax = teamData?.max_members || 3;

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-n-blue-bg text-n-blue';
      case 'admin':
        return 'bg-n-blue-bg text-n-blue';
      default:
        return 'bg-n-hover text-n-text';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active'
      ? 'bg-n-green-bg text-n-green'
      : 'bg-n-amber-bg text-n-amber';
  };

  const getStatusIcon = (status: string) => {
    return status === 'active' ? (
      <CheckCircle className="w-3.5 h-3.5" />
    ) : (
      <Clock className="w-3.5 h-3.5" />
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isError) {
    return (
      <div className="h-full flex flex-col bg-n-card overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-n-border">
          <h1 className="text-4xl font-bold text-n-text mb-1">👥 {t('team_page_title', 'Team')}</h1>
          <p className="text-sm text-n-text-md">{t('team_page_subtitle', 'Manage team members, roles, and permissions.')}</p>
        </div>
        <div className="px-6 pt-6 pb-4">
          <div className="bg-n-card border border-n-border rounded-n-sm text-center py-10">
            <div className="w-10 h-10 rounded-n flex items-center justify-center mx-auto mb-3 bg-n-red-bg">
              <AlertCircle className="w-5 h-5 text-n-red" />
            </div>
            <p className="text-sm font-bold text-n-text mb-1">{t('team_load_failed', 'Failed to load team')}</p>
            <p className="text-xs text-n-text-md">{t('team_refresh_page', 'Please try refreshing the page.')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-n-border">
        <h1 className="text-4xl font-bold text-n-text mb-1">👥 {t('team_page_title', 'Team')}</h1>
        <p className="text-sm text-n-text-md">{t('team_page_subtitle', 'Manage team members, roles, and permissions.')}</p>
      </div>
      <div className="flex-1 overflow-auto px-6 pt-6 pb-4">
        {/* Toast Messages */}
        {(error || success) && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-n shadow-n text-xs font-medium flex items-center gap-2 ${
            error ? 'bg-n-red-bg text-n-red border border-n-border' : 'bg-n-green-bg text-n-green border border-n-border'
          }`}>
            {error ? <AlertCircle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
            {error || success}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-3">
          {teamData && (
            <span className="text-[10px] font-semibold px-2 py-1 rounded-n-sm bg-n-hover text-n-text-md">
              {slotsUsed}/{slotsMax} {t('team_members_label', 'members')}
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={() => setShowInviteForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2383e2] text-white rounded-n-sm font-medium text-xs transition-colors hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('team_invite_member_btn', 'Invite Member')}
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <div className="bg-n-card rounded-n-sm border border-n-border p-4 animate-pulse">
              <div className="h-4 bg-n-hover rounded w-28 mb-3" />
              <div className="h-14 bg-n-hover rounded-n-sm" />
            </div>
            <div className="bg-n-card rounded-n-sm border border-n-border p-4 animate-pulse">
              <div className="h-4 bg-n-hover rounded w-36 mb-3" />
              <div className="space-y-2">
                <div className="h-12 bg-n-hover rounded-n-sm" />
                <div className="h-12 bg-n-hover rounded-n-sm" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Owner Card */}
            {owner && (
              <div className="bg-n-card rounded-n-sm border border-n-border">
                <div className="px-3 py-2.5 border-b border-n-border">
                  <h3 className="text-xs font-semibold text-n-text flex items-center gap-1.5">
                    <Crown className="w-3.5 h-3.5 text-n-blue" />
                    {t('team_owner_title', 'Team Owner')}
                  </h3>
                </div>
                <div className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {owner.avatar ? (
                      <img src={owner.avatar} alt={owner.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-n-blue flex items-center justify-center font-semibold text-xs text-white">
                        {getInitials(owner.name)}
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-n-text">{owner.name}</p>
                      <p className="text-[10px] text-n-text-md flex items-center gap-1">
                        <Mail className="w-2.5 h-2.5" />
                        {owner.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-n-xs text-[10px] font-semibold bg-n-blue-bg text-n-blue">{t('team_owner_role', 'Owner')}</span>
                    <span className={`px-2 py-0.5 rounded-n-xs text-[10px] font-semibold flex items-center gap-0.5 ${getStatusColor(owner.status)}`}>
                      {getStatusIcon(owner.status)}
                      {owner.status === 'active' ? t('team_active_status', 'Active') : t('team_pending_status', 'Pending')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Members Section */}
            <div className="bg-n-card rounded-n-sm border border-n-border">
              <div className="px-3 py-2.5 border-b border-n-border">
                <h3 className="text-xs font-semibold text-n-text flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-n-text-md" />
                  {t('team_members_title', 'Team Members')} ({members.length})
                </h3>
              </div>

              {members.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-10 h-10 rounded-n flex items-center justify-center mx-auto mb-3 bg-n-blue-bg">
                    <UserPlus className="w-5 h-5 text-n-blue" />
                  </div>
                  <p className="text-sm font-bold text-n-text mb-1">{t('team_no_members', 'No team members yet')}</p>
                  <p className="text-xs text-n-text-md mb-4">{t('team_invite_to_start', 'Invite someone to get started')}</p>
                  <button
                    onClick={() => setShowInviteForm(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2383e2] text-white rounded-n-sm text-xs font-medium hover:opacity-90"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t('team_invite_first_member_btn', 'Invite First Member')}
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-n-border">
                  {members.map((member: TeamMember) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 hover:bg-n-hover transition-colors group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-n-blue flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                            {getInitials(member.name)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-n-text truncate">{member.name}</p>
                          <p className="text-[10px] text-n-text-md truncate flex items-center gap-1">
                            <Mail className="w-2.5 h-2.5" />
                            {member.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        <select
                          value={member.role === 'admin' ? 'admin' : 'member'}
                          onChange={(e) => {
                            updateRoleMutation.mutate({
                              email: member.email,
                              role: e.target.value,
                            });
                          }}
                          className={`px-2 py-0.5 rounded-n-xs text-[10px] font-semibold border-0 outline-none cursor-pointer ${getRoleColor(member.role)}`}
                        >
                          <option value="member">{t('team_member_role', 'Member')}</option>
                          <option value="admin">{t('team_admin_role', 'Admin')}</option>
                        </select>

                        <span className={`px-2 py-0.5 rounded-n-xs text-[10px] font-semibold whitespace-nowrap flex items-center gap-0.5 ${getStatusColor(member.status)}`}>
                          {getStatusIcon(member.status)}
                          {member.status === 'active' ? t('team_active_status', 'Active') : t('team_pending_status', 'Pending')}
                        </span>

                        <span className="text-[10px] text-n-text-lt whitespace-nowrap flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          {new Date(member.joinedAt).toLocaleDateString()}
                        </span>

                        <button
                          onClick={() => {
                            if (confirm(t('team_confirm_remove', `Remove ${member.name} from team?`))) {
                              removeMemberMutation.mutate(member.email);
                            }
                          }}
                          disabled={removeMemberMutation.isPending}
                          className="p-1.5 text-n-text-lt hover:text-n-red hover:bg-n-red-bg rounded-n-xs transition-colors opacity-0 group-hover:opacity-100"
                          title={t('team_remove_member_title', 'Remove member')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Permissions Info Box */}
            <div className="bg-n-card border border-n-border rounded-n-sm p-3">
              <div className="flex gap-2.5">
                <Shield className="w-4 h-4 text-n-blue flex-shrink-0 mt-0.5" />
                <div className="text-[10px] flex-1">
                  <p className="font-semibold text-n-text mb-2 text-xs">{t('team_role_permissions_title', 'Role Permissions')}</p>
                  <div className="text-n-text-md space-y-1">
                    <p><span className="font-semibold text-n-text">{t('team_owner_role', 'Owner')}:</span> {t('team_owner_perms', 'Full access to all features and settings')}</p>
                    <p><span className="font-semibold text-n-text">{t('team_admin_role', 'Admin')}:</span> {t('team_admin_perms', 'Can manage content, team members, and builder')}</p>
                    <p><span className="font-semibold text-n-text">{t('team_member_role', 'Member')}:</span> {t('team_member_perms', 'Can create and edit content, view analytics')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invite Member Modal */}
        {showInviteForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-n-card rounded-n max-w-md w-full border border-n-border shadow-n">
              <div className="px-4 py-3 border-b border-n-border flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-n-text">{t('team_invite_modal_title', 'Invite Team Member')}</h3>
                  <p className="text-[10px] text-n-text-md mt-0.5">{slotsUsed}/{slotsMax} {t('team_slots_used', 'slots used')}</p>
                </div>
                <button
                  onClick={() => { setShowInviteForm(false); resetForm(); }}
                  className="p-1 hover:bg-n-hover rounded transition-colors"
                >
                  <Plus className="w-4 h-4 text-n-text-lt rotate-45" />
                </button>
              </div>

              <form onSubmit={handleInvite} className="p-4 space-y-3">
                <div>
                  <label htmlFor="invite-email" className="block text-xs font-semibold text-n-text mb-1">{t('team_invite_email_label', 'Email Address')}</label>
                  <input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="member@example.com"
                    required
                    className="w-full px-3 py-2 border border-n-border rounded-n-xs bg-n-page focus:outline-none focus:ring-1 focus:ring-n-blue text-xs"
                  />
                </div>

                <div>
                  <label htmlFor="invite-name" className="block text-xs font-semibold text-n-text mb-1">{t('team_invite_name_label', 'Full Name')}</label>
                  <input
                    id="invite-name"
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="w-full px-3 py-2 border border-n-border rounded-n-xs bg-n-page focus:outline-none focus:ring-1 focus:ring-n-blue text-xs"
                  />
                </div>

                <div>
                  <label htmlFor="invite-role" className="block text-xs font-semibold text-n-text mb-1">{t('team_invite_role_label', 'Role')}</label>
                  <select
                    id="invite-role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 border border-n-border rounded-n-xs bg-n-page focus:outline-none focus:ring-1 focus:ring-n-blue text-xs"
                  >
                    <option value="member">{t('team_invite_member_option', 'Member — Can create and edit content')}</option>
                    <option value="admin">{t('team_invite_admin_option', 'Admin — Can also manage team and settings')}</option>
                  </select>
                </div>

                {inviteMemberMutation.isError && (
                  <div className="flex items-center gap-1.5 text-[10px] text-n-red bg-n-red-bg p-2 rounded-n-xs border border-n-border">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {(inviteMemberMutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to send invitation'}
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t border-n-border">
                  <button
                    type="button"
                    onClick={() => { setShowInviteForm(false); resetForm(); }}
                    className="flex-1 px-3 py-1.5 bg-n-hover text-n-text rounded-n-xs hover:bg-n-border transition-colors text-xs font-semibold"
                  >
                    {t('team_cancel_btn', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={inviteMemberMutation.isPending || !inviteEmail.trim() || !inviteName.trim()}
                    className="flex-1 px-3 py-1.5 bg-[#2383e2] text-white rounded-n-xs disabled:opacity-60 transition-colors text-xs font-semibold hover:opacity-90"
                  >
                    {inviteMemberMutation.isPending ? t('team_sending', 'Sending...') : t('team_send_invite_btn', 'Send Invite')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPage;
